import * as ko from 'knockout';
import {
  ActionType,
  SoundName,
  TraitActionData,
  TraitActionOptionsData,
  TRAIT_ACTION_TYPES,
} from '../api/trait_actions';
import { TraitActionChoiceOptionData } from '../api/measurement_types';
import i18n from '../i18n';

export class TraitAction {
  id = ko.observable<string>(null);
  actionType = ko.observable<ActionType>(TRAIT_ACTION_TYPES.SET_VALUE).extend({ required: true });

  options = ko.observable<{
    sources: TraitActionOptionsData['sources'];
    targets: TraitActionOptionsData['targets'];
    actionTypes: TraitActionOptionsData['action_types'];
    soundNames: TraitActionOptionsData['sound_names'];
  }>();

  aiModel = ko.observable<string>(null).extend({
    required: {
      onlyIf: () => this.actionType() === TRAIT_ACTION_TYPES.CALCULATE_CANOPY_AI,
    },
  });

  prompt = ko.observable<string>(null).extend({
    required: {
      onlyIf: () => this.actionType() === TRAIT_ACTION_TYPES.CALCULATE_CANOPY_AI,
    },
  });

  sourceTraitId = ko.observable<string>(null).extend({
    required: true,
    deferred: true,
    validation: {
      validator: (value) => {
        if (!this.options()) return true; // Options not loaded yet, skip validation
        return this.options().sources.some((option) => option.id === value);
      },
      message: i18n.t('Please select one of the options.')(),
    },
  });
  targetTraitId = ko.observable<string>(null).extend({
    required: true,
    deferred: true,
    validation: {
      validator: (value) => {
        if (!this.options()) return true; // Options not loaded yet, skip validation
        return this.options().targets.some((option) => option.id === value);
      },
      message: i18n.t('Please select one of the options.')(),
    },
  });
  targetValue = ko.observable<string>(null).extend({
    required: {
      onlyIf: () => this.actionType() === TRAIT_ACTION_TYPES.SET_VALUE,
    },
    deferred: true,
    validation: {
      validator: (value) => {
        // If no value is selected or there are no options, do not flag an error
        if (!value) {
          return true;
        }
        const options = this.targetValueOptions();
        if (!options || options.length === 0) {
          return true;
        }
        return options.some((option) => option.id === value);
      },
      message: i18n.t('Please select one of the options.')(),
    },
  });
  soundName = ko.observable<SoundName>('success_sound').extend({
    required: {
      onlyIf: () => this.actionType() === TRAIT_ACTION_TYPES.PLAY_SOUND,
    },
  });

  targetValueOptions = ko.pureComputed<TraitActionChoiceOptionData[]>(() => {
    if (!this.options() || !this.targetTraitId()) {
      return [];
    }
    return this.options().targets.find((trait) => trait.id === this.targetTraitId())?.value_choices ?? [];
  });

  private errors = ko.validation.group(this);

  hasErrors = ko.pureComputed(() => {
    return this.errors().length > 0;
  });

  constructor(data?: TraitActionData) {
    if (data) {
      this.id(data.id);
      this.actionType(data.action_type);
      this.sourceTraitId(data.source_measurement_meta_id);
      this.targetTraitId(data.target_measurement_meta_id);
      this.targetValue(data.target_measurement_value);
      this.soundName(data.sound_name);
      this.aiModel(data.ai_model);
      this.prompt(data.prompt);
    }

    this.actionType.subscribe((newType) => {
      if (newType === TRAIT_ACTION_TYPES.CALCULATE_CANOPY_AI && !this.prompt()) {
        this.prompt(
          i18n.t(
            'Analyze this image to calculate the Fractional Green Canopy Cover (FGCC).\n\nProcess the image pixel by pixel. Classify a pixel as green only if it satisfies all three of the following conditions based on its RGB values:\n\n1. Red / Green < 0.95\n2. Blue / Green < 0.95\n3. (2 * Green) - Red - Blue > 20\n\nCalculate the percentage of pixels that meet these criteria relative to the total number of pixels in the image and return that percentage as a decimal value between 0 and 1.'
          )()
        );
      }
    });
  }

  setOptions(options: TraitActionOptionsData) {
    this.options({
      sources: options.sources,
      targets: options.targets,
      actionTypes: options.action_types,
      soundNames: options.sound_names ?? [],
    });
  }

  getSourceOptions() {
    return this.options()?.sources ?? [];
  }

  getPictureSourceOptions() {
    return this.getSourceOptions().filter((o) => o.type === 'picture');
  }

  getActionTypeOptions() {
    return this.options()?.actionTypes ?? [];
  }

  getTargetOptions() {
    return this.options()?.targets ?? [];
  }

  getNumberTargetOptions() {
    return this.getTargetOptions().filter((o) => o.type === 'integer' || o.type === 'decimal');
  }

  getSoundNameOptions() {
    return this.options()?.soundNames ?? [];
  }

  toData(): TraitActionData {
    return {
      action_type: this.actionType(),
      source_measurement_meta_id: this.sourceTraitId(),
      target_measurement_meta_id: this.targetTraitId(),
      target_measurement_value: this.targetValue(),
      sound_name: this.soundName(),
      ai_model: this.aiModel(),
      prompt: this.prompt(),
      ...(this.id() ? { id: this.id() } : {}),
    };
  }

  showErrors() {
    this.errors.showAllMessages();
  }

  usesTrait(traitId: string) {
    return this.sourceTraitId() === traitId || this.targetTraitId() === traitId;
  }
}
